import React, { useEffect, useState } from "react";
import axios from "axios";
import { ListVideo, Plus, Loader2, XCircle, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Q_UI = {
  queued: { label: "En cola", cls: "bg-[#F0EBE4] text-[#8A7F73]" },
  running: { label: "Procesando", cls: "bg-[#FFF3E9] text-[#C2570F]" },
  done: { label: "Completado", cls: "bg-[#EAF7EE] text-[#1E6B3A]" },
  error: { label: "Error", cls: "bg-[#FDECEA] text-[#B3261E]" },
  cancelled: { label: "Cancelado", cls: "bg-[#F0EBE4] text-[#8A7F73]" },
};

export const QueuePanel = ({ videos }) => {
  const [items, setItems] = useState([]);
  const [vid, setVid] = useState("");
  const [mode, setMode] = useState("both");
  const [style, setStyle] = useState("box");
  const [strength, setStrength] = useState(3);
  const [mins, setMins] = useState(1);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/queue`);
      setItems(data.items || []);
    } catch {}
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []);

  const add = async () => {
    if (!vid) { toast("Elige un vídeo para encolar."); return; }
    try {
      await axios.post(`${API}/queue`, { video_id: vid, mode, style: `${style}|${strength}`, chunk_minutes: mins, auto_export: true });
      toast.success("Añadido a la cola: se analizará y exportará solo");
      setVid("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo encolar.");
    }
  };

  const remove = async (id, status) => {
    try {
      const { data } = await axios.delete(`${API}/queue/${id}`);
      toast(data.status === "cancelling" ? "Cancelando el trabajo en curso…" : "Quitado de la cola");
      load();
    } catch {}
  };

  const selectable = (videos || []).filter((v) => v.has_original);
  const active = items.filter((i) => ["queued", "running"].includes(i.status));

  return (
    <section data-testid="queue-panel" className="mb-8 rounded-3xl bg-white border border-[#F0E2D2] p-5">
      <div className="flex items-center gap-2 mb-3">
        <ListVideo size={16} className="text-[#F26F21]" />
        <p className="text-[12px] font-extrabold tracking-[0.12em] uppercase text-[#C2570F]">Cola de lotes</p>
        <p className="text-[11.5px] text-[#8A7F73] font-semibold">— los vídeos se analizan y exportan solos, uno tras otro</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select data-testid="queue-video-select" value={vid} onChange={(e) => setVid(e.target.value)}
          className="h-9 flex-1 min-w-[200px] rounded-lg border border-[#F0E2D2] bg-[#FFF7F0] px-2 text-[12.5px] font-bold text-[#241E18]">
          <option value="">Elegir vídeo…</option>
          {selectable.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select data-testid="queue-mode-select" value={mode} onChange={(e) => setMode(e.target.value)}
          className="h-9 rounded-lg border border-[#F0E2D2] bg-[#FFF7F0] px-2 text-[12px] font-bold text-[#241E18]">
          <option value="both">Personas + Matrículas</option>
          <option value="plates">Matrículas</option>
          <option value="faces">Personas</option>
        </select>
        <select data-testid="queue-style-select" value={style} onChange={(e) => setStyle(e.target.value)}
          className="h-9 rounded-lg border border-[#F0E2D2] bg-[#FFF7F0] px-2 text-[12px] font-bold text-[#241E18]">
          <option value="box">Difuminado</option>
          <option value="pixel">Pixelado</option>
          <option value="oval">Óvalo</option>
        </select>
        <select data-testid="queue-strength-select" value={strength} onChange={(e) => setStrength(Number(e.target.value))}
          className="h-9 rounded-lg border border-[#F0E2D2] bg-[#FFF7F0] px-2 text-[12px] font-bold text-[#241E18]">
          <option value={1}>Suave</option>
          <option value={2}>Media</option>
          <option value={3}>Fuerte</option>
        </select>
        <select data-testid="queue-mins-select" value={mins} onChange={(e) => setMins(Number(e.target.value))}
          className="h-9 rounded-lg border border-[#F0E2D2] bg-[#FFF7F0] px-2 text-[12px] font-bold text-[#241E18]">
          <option value={1}>Tramos 1 min</option>
          <option value={2}>Tramos 2 min</option>
          <option value={5}>Tramos 5 min</option>
        </select>
        <button data-testid="queue-add-btn" onClick={add}
          className="h-9 px-4 rounded-full bg-[#F26F21] text-white text-[12.5px] font-extrabold hover:bg-[#D95F14] transition-colors inline-flex items-center gap-1.5">
          <Plus size={13} /> Encolar
        </button>
      </div>
      {items.length === 0 ? (
        <p className="text-[11.5px] text-[#8A7F73] font-semibold">La cola está vacía.</p>
      ) : (
        <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
          {items.map((it) => {
            const ui = Q_UI[it.status] || Q_UI.queued;
            return (
              <div key={it.id} data-testid={`queue-item-${it.id}`} className="flex flex-wrap items-center gap-2.5 rounded-xl bg-[#FFF7F0] border border-[#F5E7D8] px-3 py-2">
                {it.status === "running" ? <Loader2 size={13} className="text-[#C2570F] animate-spin shrink-0" />
                  : it.status === "done" ? <CheckCircle2 size={13} className="text-[#2FA45A] shrink-0" />
                  : it.status === "error" ? <AlertTriangle size={13} className="text-[#B3261E] shrink-0" />
                  : <Clock size={13} className="text-[#8A7F73] shrink-0" />}
                <span className="text-[12.5px] font-extrabold text-[#241E18] truncate max-w-[220px]">{it.video_name}</span>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-extrabold ${ui.cls}`}>{ui.label}</span>
                <span className="flex-1 text-[11px] font-semibold text-[#8A7F73] truncate">
                  {it.status === "running" ? `${it.progress || 0}% · ${it.step || ""}` : it.status === "error" ? (it.error || "") : ""}
                </span>
                {["queued", "running"].includes(it.status) && (
                  <button data-testid={`queue-remove-${it.id}`} onClick={() => remove(it.id, it.status)}
                    className="h-7 px-2.5 rounded-lg border border-[#E4574A]/40 text-[#D93025] text-[11px] font-bold hover:bg-[#E4574A]/10 transition-colors inline-flex items-center gap-1">
                    <XCircle size={11} /> {it.status === "running" ? "Cancelar" : "Quitar"}
                  </button>
                )}
              </div>
            );
          })}
          {active.length > 1 && (
            <p className="text-[10.5px] text-[#8A7F73] font-semibold pt-1">Se procesa uno a la vez para no saturar la máquina.</p>
          )}
        </div>
      )}
    </section>
  );
};

export default QueuePanel;
